"use client";
import React, { useState } from 'react';
import AppLayout from "@/components/layout/AppLayout";
import Link from "next/link";
import Image from 'next/image';
import { ContactPageSchema } from '@/components/seo/SchemaMarkup';
import SEOLinks from '@/components/seo/SEOLinks';
import { trackFormSubmission } from '@/lib/analytics';
import siteConfig from '@/config/siteConfig';

// Interface for form data
interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  message: string;
  botField: string;
  privacy: boolean;
}

// Interface for the debounce function's shape
// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface DebounceFunction {
  (form: ContactFormElement): Promise<void>;
}

// Interface for the message state
interface FormMessage {
  text: string;
  type: 'success' | 'error' | null;
}

// Interface for the form element
interface ContactFormElement extends HTMLFormElement {
  firstName: HTMLInputElement;
  lastName: HTMLInputElement;
  email: HTMLInputElement;
  message: HTMLTextAreaElement;
  botField: HTMLInputElement;
  privacy: HTMLInputElement;
}

// Custom debounce function with TypeScript types
const debounce = (func: (form: ContactFormElement) => Promise<void>, wait: number) => {
  let timeout: NodeJS.Timeout;
  return (form: ContactFormElement) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(form), wait);
  };
};

export default function ContactPage() {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    message: '',
    botField: '',
    privacy: false,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<FormMessage>({
    text: '',
    type: null,
  });
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);

  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' && 'checked' in e.target ? e.target.checked : false;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Async function to handle the actual form submission
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const submitForm = async (form: ContactFormElement) => {
    const { firstName, lastName, email, message, botField, privacy } = formData;

    // Honeypot check: if botField is filled, likely a bot
    if (botField) {
      setMessage({ text: 'Submission blocked. Please try again.', type: 'error' });
      return;
    }

    // Basic validation
    if (!firstName || !lastName || !message || !privacy) {
      setMessage({ text: 'Please fill out all required fields, including the privacy agreement.', type: 'error' });
      return;
    }  

    // Email format check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setMessage({ text: 'Please enter a valid email address.', type: 'error' });
      return;
    }

    setIsLoading(true);
    setMessage({ text: '', type: null }); // Clear previous message

    try {
      const response = await fetch('/api/forms/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setMessage({ text: 'Message sent successfully!', type: 'success' });
        setShowConfirmation(true); // Show confirmation popup
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          message: '',
          botField: '',
          privacy: false,
        });
          trackFormSubmission('Contact Form', true);
      } else {
        const errorData = await response.json();
        setMessage({ text: errorData.error || 'Failed to send message. Please try again.', type: 'error' });
        trackFormSubmission('Contact Form', false);
      }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      setMessage({ text: 'An error occurred. Please try again later.', type: 'error' });
      trackFormSubmission('Contact Form', false);
    } finally {
      setIsLoading(false);
    }
  };

  // Debounce the submission (e.g., 500ms delay)
  const debouncedSubmit = debounce((form: ContactFormElement) => submitForm(form), 500);

  // Handle form submission event
  const handleSubmit = (e: React.FormEvent<ContactFormElement>) => {
    e.preventDefault();
    const form = e.target as ContactFormElement;
    debouncedSubmit(form);
  };

  // Close confirmation popup
  const closeConfirmation = () => {
    setShowConfirmation(false);
    setMessage({ text: '', type: null }); // Clear success message
  };

  return (
    <>
      <meta name="author" content={siteConfig.authors} />
      <meta property="article:published_time" content={siteConfig.publishedTime} />
      <meta property="fb:app_id" content={siteConfig.FacebookAppID} />
      <ContactPageSchema />
      <SEOLinks customPath="/contact" />
      <AppLayout>
        {/* Hero Section */}
        <section className="gradient-bg text-white">
          <div className="container py-16 md:py-24">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h1 className="text-white mb-6">
                  Contact Us
                </h1>
                <p className="text-lg mb-8 text-gray-100">
                  Get in touch with our team to learn how Perxify's commerce cloud platform can help your business grow.
                </p>
              </div>
              <div className="hidden md:block">
                <div className="bg-white/10 rounded-lg h-80 flex items-center justify-center">
                  <Image
                    src="/images/hero/contact-visualization.jpg"
                    alt="Contact Visualization"
                    width={600}
                    height={320}
                    priority={true}
                    fetchPriority="high"
                    loading="eager"
                    className="rounded-lg"
                    sizes="(max-width: 768px) 100vw, 50vw"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="section bg-gray-50">
          <div className="container">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="mb-4">
                  Send Us a Message
                </h2>
                <p className="mb-8 text-gray-800">
                  Have questions about our platform? Fill out the form below and we will get back to you as soon as possible.
                </p>

                {/* Form Status Messages with ARIA Live Region */}
                <div 
                  role="status" 
                  aria-live="polite" 
                  aria-atomic="true"
                  className="mb-4"
                >
                  {message.text && (
                    <div 
                      className={`p-4 rounded-md ${
                        message.type === 'success' 
                          ? 'bg-green-50 text-green-800 border border-green-200' 
                          : 'bg-red-50 text-red-800 border border-red-200'
                      }`}
                      role="alert"
                    >
                      {message.text}
                    </div>
                  )}
                </div>

                <form 
                  onSubmit={handleSubmit} 
                  className="space-y-6"
                  aria-busy={isLoading}
                  noValidate
                >
                  {/* Honeypot field - hidden from users but visible to bots */}
                  <div style={{ display: 'none' }} aria-hidden="true">
                    <label htmlFor="botField">Leave this field empty</label>
                    <input
                      type="text"
                      name="botField"
                      id="botField"
                      tabIndex={-1}
                      autoComplete="off"
                      value={formData.botField}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="first-name" className="block text-sm font-medium text-gray-900 mb-1">
                        First Name*
                      </label>
                      <input 
                        type="text" 
                        id="first-name" 
                        name="firstName" 
                        required 
                        value={formData.firstName}
                        onChange={handleChange}
                        autoComplete="given-name"
                        aria-required="true"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00A4BD] focus:border-[#00A4BD] text-gray-900 transition-colors" 
                        disabled={isLoading}
                      />
                    </div>
                    <div>
                      <label htmlFor="last-name" className="block text-sm font-medium text-gray-900 mb-1">
                        Last Name*
                      </label>
                      <input 
                        type="text" 
                        id="last-name" 
                        name="lastName" 
                        required 
                        value={formData.lastName}
                        onChange={handleChange}
                        autoComplete="family-name"
                        aria-required="true"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00A4BD] focus:border-[#00A4BD] text-gray-900 transition-colors" 
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-1">
                      Email Address*
                    </label>
                    <input 
                      type="email" 
                      id="email" 
                      name="email" 
                      required 
                      value={formData.email}
                      onChange={handleChange}
                      autoComplete="email"
                      aria-required="true"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00A4BD] focus:border-[#00A4BD] text-gray-900 transition-colors" 
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-900 mb-1">
                      Message*
                    </label>
                    <textarea 
                      id="message" 
                      name="message" 
                      rows={5} 
                      required 
                      value={formData.message}
                      onChange={handleChange}
                      aria-required="true"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00A4BD] focus:border-[#00A4BD] text-gray-900 transition-colors" 
                      disabled={isLoading}
                    ></textarea>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input 
                        id="privacy" 
                        name="privacy" 
                        type="checkbox" 
                        checked={formData.privacy}
                        onChange={handleChange}
                        required 
                        aria-required="true"
                        aria-describedby="privacy-text"
                        className="h-4 w-4 text-[#00A4BD] focus:ring-2 focus:ring-[#00A4BD] border-gray-300 rounded transition-colors" 
                        disabled={isLoading}
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="privacy" id="privacy-text" className="text-gray-800">
                        I agree to the <Link href="/privacy-policy" className="text-[#00A4BD] hover:underline">Privacy Policy</Link> and consent to Perxify processing my data for the purpose of contacting me.
                      </label>
                    </div>
                  </div>
                  
                  <div>
                    <button 
                      type="submit" 
                      className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed transition-opacity focus:ring-2 focus:ring-offset-2 focus:ring-[#00A4BD]"
                      disabled={isLoading}
                      aria-busy={isLoading}
                      aria-describedby={isLoading ? "submit-status" : undefined}
                    >
                      {isLoading ? (
                        <>
                          <span className="inline-flex items-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Sending...
                          </span>
                          <span className="sr-only" id="submit-status">Form is being submitted, please wait</span>
                        </>
                      ) : (
                        'Send Message'
                      )}
                    </button>
                  </div>
                </form>
              </div>
                  
              <div>
                <div className="bg-white p-8 rounded-lg shadow-sm">
                  <h3 className="text-xl mb-6 text-gray-900">
                    Contact Information
                  </h3>
                  
                  <div className="space-y-6">
                    <div className="flex items-start">
                      <div className="w-10 h-10 bg-[#E6F7F9] rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#00A4BD]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-lg font-medium mb-1 text-gray-900">
                          Email
                        </h4>
                        <a href="mailto:hello@perxify.com" className="text-[#00A4BD] hover:underline">hello@perxify.com</a>
                      </div>
                    </div>
              
                    <div className="flex items-start">
                      <div className="w-10 h-10 bg-[#E6F7F9] rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#00A4BD]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-lg font-medium mb-1 text-gray-900">
                          Location
                        </h4>
                        <p className="text-gray-700">
                          Tallinn, Estonia
                        </p>
                      </div>
                    </div>
                  </div>
                    
                  <div className="mt-8">
                    <h3 className="text-xl mb-4 text-gray-900">
                      Connect With Us
                    </h3>
                    <div className="flex space-x-4">
                      <a 
                        href={siteConfig.organization.social.linkedin}
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="w-10 h-10 bg-[#E6F7F9] rounded-full flex items-center justify-center text-[#00A4BD] hover:bg-[#00A4BD] hover:text-white transition-colors focus:ring-2 focus:ring-[#00A4BD] focus:ring-offset-2"
                        aria-label="Follow us on LinkedIn"
                      >
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                        </svg>
                      </a>
                      <a 
                        href={siteConfig.organization.social.x}
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="w-10 h-10 bg-[#E6F7F9] rounded-full flex items-center justify-center text-[#00A4BD] hover:bg-[#00A4BD] hover:text-white transition-colors focus:ring-2 focus:ring-[#00A4BD] focus:ring-offset-2"
                        aria-label="Follow us on X (Twitter)"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M18.205 2.25h3.308l-7.227 8.26 8.502 11.24h-6.586l-4.785-6.29L6.48 20.5H3l5.35-6.112L1.5 2.25h6.657l4.356 5.757 4.692-5.757zm-1.16 17.52h1.833L7.045 4.126H5.078L16.045 19.77Z" />
                        </svg>
                      </a>
                      <a 
                        href={siteConfig.organization.social.facebook} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="w-10 h-10 bg-[#E6F7F9] rounded-full flex items-center justify-center text-[#00A4BD] hover:bg-[#00A4BD] hover:text-white transition-colors focus:ring-2 focus:ring-[#00A4BD] focus:ring-offset-2"
                        aria-label="Follow us on Facebook"
                      >
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
                  
        {/* CTA Section */}
        <section className="gradient-bg text-white">
          <div className="container py-16 text-center">
            <h2 className="text-white mb-6">
              Ready to Transform Your Commerce Experience?
            </h2>
            <p className="text-lg mb-8 text-gray-100 max-w-3xl mx-auto">
              Schedule a personalized demo to see how Perxify can help your business grow.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/contact/schedule-demo" className="btn-cta inline-block">
                Schedule a Demo
              </Link>
            </div>
          </div>
        </section>

        {/* Confirmation Popup */}
        {showConfirmation && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" 
            role="dialog" 
            aria-modal="true"
            aria-labelledby="confirmation-title"
            aria-describedby="confirmation-description"
          >
            <div className="bg-white rounded-lg p-8 max-w-md w-full">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-[#E6F7F9] rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-[#00A4BD]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 id="confirmation-title" className="text-2xl font-bold mb-2 text-gray-900">Thank You!</h3>
                <p id="confirmation-description" className="text-gray-700">
                  Your message has been sent successfully. A member of our team will get back to you as soon as possible.
                </p>
              </div>
              <div className="text-center">
                <button
                  onClick={closeConfirmation}
                  className="btn-primary"
                  autoFocus
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </AppLayout>
    </>
  );
}